import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { PublicLayout } from '@/components/PublicLayout';
import { Kicker } from '@/components/Kicker';
import { CheckCircle2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';

export default function RequestPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    request_type: params.get('type') === 'offer' ? 'offer' : 'sample',
    quantity: '',
    contact_name: user?.full_name || '',
    contact_email: user?.email || '',
    company: user?.company || '',
    phone: '',
    message: '',
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, contact_name: user.full_name || f.contact_name, contact_email: user.email || f.contact_email, company: user.company || f.company }));
  }, [user]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/requests', { ...form, product_id: parseInt(id, 10) });
      setSent(true);
      toast.success(t('request.success'));
    } catch (err) {
      toast.error(t('request.error'));
    } finally {
      setLoading(false);
    }
  };

  const input = 'h-11 w-full rounded-lg border border-[#E4E9F0] bg-white px-3 text-sm outline-none focus:border-[#E2611C]';

  if (sent) {
    return (
      <PublicLayout>
        <section className="pn-container py-20">
          <div className="pn-card mx-auto max-w-lg p-10 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-[#1F7A4D]" />
            <h2 className="mt-4 font-display text-2xl font-semibold">{t('request.success')}</h2>
            <div className="mt-6 flex justify-center gap-3">
              <Link to={`/product/${id}`} className="pn-btn-outline">{t('request.back')}</Link>
              <Link to="/catalog" className="pn-btn-dark">{t('nav.catalog')}</Link>
            </div>
          </div>
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="pn-container py-14">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-[#4A4D52] hover:text-[#14171C]">
          <ArrowLeft className="h-4 w-4" /> {t('common.back')}
        </button>
        <div className="mt-4 max-w-2xl">
          <Kicker>{t('request.title')}</Kicker>
          <h1 className="mt-2 font-display text-3xl font-semibold text-[#14171C] sm:text-4xl">{t('request.title')}</h1>
        </div>
        <form onSubmit={onSubmit} className="mt-8 max-w-2xl pn-card p-6 md:p-8" data-testid="request-form">
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#4A4D52]">{t('request.type')}</label>
          <div className="flex gap-2" data-testid="request-type-radio">
            {['sample', 'offer'].map((v) => (
              <button
                type="button"
                key={v}
                onClick={() => setForm({ ...form, request_type: v })}
                data-testid={`request-type-${v}`}
                className={`flex-1 rounded-full border px-4 py-2 text-sm font-medium transition ${form.request_type === v ? 'border-[#E2611C] bg-[#FBEADD] text-[#E2611C]' : 'border-[#E4E9F0] bg-white text-[#14171C] hover:border-[#14171C]/25'}`}
              >
                {t(`request.${v}`)}
              </button>
            ))}
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#4A4D52]">{t('request.contact_name')}</label>
              <input required data-testid="request-name" className={input} value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#4A4D52]">{t('request.contact_email')}</label>
              <input required type="email" data-testid="request-email" className={input} value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#4A4D52]">{t('request.company')}</label>
              <input data-testid="request-company" className={input} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#4A4D52]">{t('request.phone')}</label>
              <input data-testid="request-phone" className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#4A4D52]">{t('request.quantity')}</label>
              <input data-testid="request-quantity" className={input} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="e.g. 250 kg" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#4A4D52]">{t('request.message')}</label>
              <textarea data-testid="request-message" rows={5} className="w-full rounded-lg border border-[#E4E9F0] bg-white p-3 text-sm outline-none focus:border-[#E2611C]" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-[#4A4D52]">{t('product.price_on_request')}</div>
            <button disabled={loading} type="submit" data-testid="request-submit-button" className="pn-btn-orange disabled:opacity-60">
              {loading ? t('common.loading') : t('request.submit')}
            </button>
          </div>
        </form>
      </section>
    </PublicLayout>
  );
}
